import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
} from "react";
import { formatDateDisplay } from "../utils/dateUtils";
import { clearExtraConfirmations } from "../hooks/useDataAccuracyConfirmation";

export type AppScreen =
	// Authentication screens
	| "auth-sign-in"
	| "auth-create-account"
	| "auth-verify-email"
	| "auth-phone-number"
	| "auth-verify-phone"
	| "auth-setup-password"
	| "auth-complete-profile"
	// Main dashboard
	| "dashboard"
	// My Event screen
	| "my-event"

	// Core application screens
	| "app-profile-completion"
	| "occupation-proof"
	| "work-permit-proof"
	| "app-profile-completion-overview"
	| "app-payroll-data"
	| "app-prm-data"
	| "app-contract-data"
	// Onboarding flow screens
	| "onboarding-welcome"
	| "onboarding-step-profile"
	| "onboarding-step-contract-data"
	| "onboarding-step-event-application"
	| "onboarding-step-code-of-conduct"
	| "onboarding-step-payroll-info"
	| "onboarding-step-id-check"
	// Onboarding intro screens
	| "intro-profile-completion"
	| "intro-contract-data"
	| "intro-event-application"
	| "intro-code-of-conduct"
	| "intro-payroll-info"
	| "intro-id-check"
	| "app-event-application"
	| "app-code-of-conduct"
	| "app-payroll-info"
	| "app-event-details-1"
	| "app-event-details-2"
	| "app-group-select"
	| "app-group-create"
	| "app-group-join"
	| "app-group"
	| "app-notifications"
	| "app-contract"
	| "app-info-faq"
	| "app-settings"
	// Operational screens
	| "operations-check-in"
	| "operations-check-out"
	// User account management
	| "delete-my-account"
	// Legacy screens (maintain compatibility)
	| "profile-data"
	| "financial-data"
	| "my-profile"
	| "billing-data"
	| "payroll-document"
	| "job-detail"
	| "task-list"
	| "settings"
	| "notifications"
	| "appointments"
	| "webinar-booking"
	| "training-booking"
	| "check-in-list"
	| "check-out-list"
	| "apply"
	| "id-terms"
	| "id-capture"
	| "id-result";

export interface UserProfile {
	firstName: string;
	lastName: string;
	email: string;
	phone: string;
	uid: string;
	dateOfBirth: string;
	nationality: string;
	occupation?: string;
	germanLevel: string;
	englishLevel?: string;
	taxClass?: string;
	employmentType?: string;
	religion?: string;
	socialSecurityNumber?: string;
	iban?: string;
	accountHolder?: string;
	taxId?: string;
	bankAccount?: string;
	street?: string;
	postalCode?: string;
	city?: string;
	cityOfBirth?: string;
	country?: string;
	healthCertificate?: string;
	company?: string;
	department?: string;
	flughafenausweisnummer?: string;
	ausweis_color?: string;
	id_number?: string;
	accepts_64_day_rule?: boolean;
	confirmed_unallowed_occupation?: boolean;
}

export interface Job {
	id: string;
	title: string;
	company: string;
	location: string;
	date: string;
	startTime?: string; // Add start time for timezone-aware date handling
	time: string;
	status: "check-in-required" | "pending" | "completed" | "incomplete";
	tasks?: Task[];
}

export interface Task {
	id: string;
	title: string;
	completed: boolean;
	jobId: string;
}

export interface Contract {
	id: string;
	title: string;
	date: string;
	status: "pending" | "signed";
	downloadUrl?: string;
}

export interface PayrollDocument {
	id: string;
	title: string;
	date: string;
	downloadUrl: string;
}

export interface Appointment {
	id: string;
	title: string;
	date: string;
	time: string;
	location: string;
	daysUntil: string;
}

export interface StaffMember {
	id: string;
	name: string;
	idCard: string;
	checkedIn: boolean;
	checkInTime?: string;
	checkedOut: boolean;
	checkOutTime?: string;
}

export interface SupervisorTask {
	id: string;
	title: string;
	description: string;
	completed: boolean;
	type: "supervisor" | "regular";
	actionText?: string;
	actionScreen?: string;
	supervisorType?: "check-in" | "check-out";
}

export interface IDCheckState {
	status: "Pending" | "Completed";
	uploadDate?: string;
	replacementsRemaining: number;
	navigationContext?: "initial" | "replacement";
}

export interface ProcessCompletionState {
	// Step 1: Choose Availability (Apply Tab) - FIRST USER ACTION
	availabilityCompleted: boolean;
	appliedDate?: string; // Date when user completed availability selection
	// Step 2: Complete Financial Profile - SECOND USER ACTION
	financialProfileCompleted: boolean;
	// Step 3: Complete Profile Data - THIRD USER ACTION
	profileDataCompleted: boolean;
	// Step 4: Automatic Booking Results (display/view) - SYSTEM PROCESS
	automaticBookingCompleted: boolean;
	// Step 5: Contract Process (mandatory before ID check) - NEW
	contractCompleted: boolean;
	// Step 6: Complete ID Check (mandatory before work)
	idCheckCompleted: boolean;
	// Step 7: Work Ready (user onboarding complete)
	workReadyStatus: boolean;
	// OPERATIONAL: Work Complete (separate from onboarding - for event/job completion tracking)
	workCompleted: boolean;
	// Additional tracking
	hasBookedShifts: boolean;
	requiresTraining: boolean;
}

export interface ContractState {
	status:
		| "download_required"
		| "sign_and_send"
		| "ucm_processing"
		| "ucm_ready"
		| "complete";
	contractId?: string;
	downloadUrl?: string;
	ucmDownloadUrl?: string;
	submittedDate?: string;
	ucmProcessedDate?: string;
}

// User completion states for access control
export interface UserCompletionState {
	profile_complete: boolean;
	contract_complete: boolean;
	payroll_complete: boolean;
	event_applied: boolean;
	event_confirmed: boolean;
	group_member: boolean;
	checked_in: boolean;
	id_check_complete: boolean;
}

export interface OnboardingState {
	isFirstTimeUser: boolean;
	hasCompletedOnboarding: boolean;
	currentOnboardingStep: number;
	skippedSteps: string[];
	onboardingProgress: Record<string, OnboardingStepProgress>;
}

export interface OnboardingStepProgress {
	stepNumber: number;
	completed: boolean;
	skipped: boolean;
	data?: any;
	completedAt: string;
}

export interface OnboardingStepInfo {
	step: number;
	id: string;
	title: string;
	route: AppScreen;
	nextStep: number | null;
}

export type UserType = "new_signup" | "existing_login" | "booked_user";

export interface NavigationContext {
	sourceScreen: AppScreen;
	sourceEventId?: string;
	selectedJobIndex?: number;
}

export interface TaskItem {
	id: string;
	title: string;
	status: "completed" | "action-required" | "info";
	screen: AppScreen;
	description?: string;
	requirements?: string[];
	showProgress?: boolean;
	progressValue?: number;
}

// Group management interfaces
export interface GroupMember {
	id: string;
	name: string;
	email: string;
	isOwner: boolean;
}

export interface Group {
	id: string;
	name: string;
	description: string;
	groupNumber: string;
	privacy: "public" | "private";
	ownerId: string;
	members: GroupMember[];
	maxMembers: number;
}

export type GroupState =
	| "no-group"
	| "creating"
	| "joining"
	| "owner"
	| "member"
	| "solo";

export interface AppContextType {
	currentScreen: AppScreen;
	setCurrentScreen: (screen: AppScreen) => void;
	userProfile: UserProfile | null;
	setUserProfile: (profile: UserProfile | null) => void;
	jobs: Job[];
	setJobs: (jobs: Job[]) => void;
	selectedJob: Job | null;
	setSelectedJob: (job: Job | null) => void;
	contracts: Contract[];
	setContracts: (contracts: Contract[]) => void;
	payrollDocuments: PayrollDocument[];
	setPayrollDocuments: (documents: PayrollDocument[]) => void;
	appointments: Appointment[];
	setAppointments: (appointments: Appointment[]) => void;
	staffMembers: StaffMember[];
	setStaffMembers: (staff: StaffMember[]) => void;
	supervisorTasks: SupervisorTask[];
	setSupervisorTasks: (tasks: SupervisorTask[]) => void;
	updateTaskCompletion: (taskId: string, completed: boolean) => void;
	isAuthenticated: boolean;
	setIsAuthenticated: (authenticated: boolean) => void;
	idCheckState: IDCheckState;
	updateIDCheckState: (updates: Partial<IDCheckState>) => void;
	completeIDCheck: () => void;
	deleteIDDocument: () => void;
	useReplacement: () => boolean;
	startReplacement: () => void;
	processState: ProcessCompletionState;
	updateProcessState: (updates: Partial<ProcessCompletionState>) => void;
	canAccessScreen: (screen: AppScreen) => {
		allowed: boolean;
		message?: string;
		redirectTo?: AppScreen;
		requirements?: string[];
	};
	navigateWithProcessCheck: (screen: AppScreen) => void;
	navigateDirectly: (screen: AppScreen) => void;
	navigateToProfileCompletion: () => void;
	navigateWithReturnTo: (screen: AppScreen, returnScreen?: AppScreen) => void;
	getCurrentStep: () => { step: number; title: string; description: string };
	triggerAutomaticBooking: () => void;
	// User state management
	userType: UserType;
	setUserType: (type: UserType) => void;
	contractState: ContractState;
	updateContractState: (updates: Partial<ContractState>) => void;
	downloadContract: () => void;
	markContractAsSent: () => void;
	downloadUCMContract: () => void;
	completeContractProcess: () => void;
	initializeExistingUser: () => void;
	loadExistingUserData: () => void;
	// Navigation context management
	navigationContext: NavigationContext | null;
	setNavigationContext: (context: NavigationContext | null) => void;
	navigateToMyEventWithContext: () => void;
	// New access control and task management
	userCompletionState: UserCompletionState;
	updateUserCompletionState: (updates: Partial<UserCompletionState>) => void;
	getDashboardTasks: () => TaskItem[];
	// Event status badge functions
	getEventStatus: (
		eventIndex: number
	) =>
		| "unavailable"
		| "contract-missing"
		| "checkin-ready"
		| "ready-work"
		| "approved"
		| "payrolled";
	getCombinedEventStatus: () =>
		| "unavailable"
		| "contract-missing"
		| "checkin-ready"
		| "ready-work"
		| "approved"
		| "payrolled";
	getCombinedEventBadge: () => string;
	// Onboarding management
	onboardingState: OnboardingState;
	updateOnboardingState: (updates: Partial<OnboardingState>) => void;
	isFirstTimeUser: () => boolean;
	getProfileCompletionProgress: () => {
		completed: number;
		total: number;
		percentage: number;
	};
	checkFirstTimeOnboarding: () => void;
	// Sequential onboarding functions
	getOnboardingSequence: () => OnboardingStepInfo[];
	getCurrentStepInfo: (stepNumber: number) => OnboardingStepInfo | undefined;
	goToNextStep: (currentStep: number) => void;
	continueToNextStep: (currentStep: number, taskData?: any) => void;
	skipToNextStep: (currentStep: number) => void;
	saveTaskCompletion: (
		stepNumber: number,
		data: any,
		isSkipped: boolean
	) => void;
	completeOnboarding: () => void;
	getAccessRequirements: (screen: AppScreen) => string[];
	// Group management functions
	getUserGroupStatus: () => {
		groupState: GroupState;
		group: Group | null;
		isSolo: boolean;
	};
	createGroup: (
		groupName: string,
		description: string,
		privacy: "public" | "private"
	) => Promise<Group>;
	joinGroup: (groupNumber: string) => Promise<Group>;
	leaveGroup: () => Promise<void>;
	deleteGroup: () => Promise<void>;
	kickMember: (memberId: string) => Promise<void>;
	setSoloParticipation: () => void;
	logout: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
	// Check if user is already authenticated on app load
	const hasToken = !!localStorage.getItem("accessToken");

	const [currentScreen, setCurrentScreen] = useState<AppScreen>(
		hasToken ? "dashboard" : "auth-sign-in"
	);

	// Load user profile from localStorage if available
	const [userProfile, setUserProfile] = useState<UserProfile | null>(() => {
		if (hasToken) {
			const storedUser = localStorage.getItem("currentUser");
			if (storedUser) {
				try {
					const user = JSON.parse(storedUser);
					return {
						firstName: user.firstName || "",
						lastName: user.lastName || "",
						email: user.email || "",
						phone: user.phone || "",
						uid: user.extraId?.toString() || "",
						dateOfBirth: user.dateOfBirth || "",
						nationality: user.nationality || "",
						germanLevel: user.germanLevel || "",
						englishLevel: user.englishLevel || "",
						taxClass: user.taxClass || "",
						employmentType: user.employmentType || "",
						religion: user.religion || "",
						socialSecurityNumber: user.socialSecurityNumber || "",
						iban: user.iban || "",
						accountHolder: user.accountHolder || "",
						healthCertificate: user.healthCertificate || "",
					};
				} catch (e) {
					console.error("Failed to parse stored user:", e);
				}
			}
		}
		return null;
	});
	const [jobs, setJobs] = useState<Job[]>([]);
	const [selectedJob, setSelectedJob] = useState<Job | null>(null);
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [payrollDocuments, setPayrollDocuments] = useState<PayrollDocument[]>(
		[]
	);
	const [appointments, setAppointments] = useState<Appointment[]>([]);
	const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
	const [supervisorTasks, setSupervisorTasks] = useState<SupervisorTask[]>([]);
	// Check for existing valid token on initialization
	const [isAuthenticated, setIsAuthenticated] = useState(() => {
		const token = localStorage.getItem("accessToken");
		return !!token;
	});
	const [userType, setUserType] = useState<UserType>("new_signup");
	const [idCheckState, setIdCheckState] = useState<IDCheckState>({
		status: "Pending",
		replacementsRemaining: 2,
		navigationContext: "initial",
	});
	const [processState, setProcessState] = useState<ProcessCompletionState>({
		availabilityCompleted: false,
		appliedDate: undefined,
		financialProfileCompleted: false,
		profileDataCompleted: false,
		automaticBookingCompleted: false,
		contractCompleted: false,
		idCheckCompleted: false,
		workReadyStatus: false,
		workCompleted: false,
		hasBookedShifts: false,
		requiresTraining: false,
	});
	const [contractState, setContractState] = useState<ContractState>({
		status: "download_required",
	});
	const [navigationContext, setNavigationContext] =
		useState<NavigationContext | null>(null);
	const [userCompletionState, setUserCompletionState] =
		useState<UserCompletionState>({
			profile_complete: false,
			contract_complete: false,
			payroll_complete: false,
			event_applied: false,
			event_confirmed: false,
			group_member: false,
			checked_in: false,
			id_check_complete: false,
		});

	const [onboardingState, setOnboardingState] = useState<OnboardingState>({
		// If user has a token on load, they're not a first time user (returning user)
		isFirstTimeUser: !hasToken,
		hasCompletedOnboarding: hasToken, // If they have a token, they've completed onboarding
		currentOnboardingStep: 0,
		skippedSteps: [],
		onboardingProgress: {},
	});

	// Validate token on mount and fetch fresh user data
	useEffect(() => {
		const validateAuth = async () => {
			const token = localStorage.getItem("accessToken");
			if (token) {
				try {
					// Try to fetch current user to validate token
					const response = await fetch(
						`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/auth/me`,
						{
							headers: {
								Authorization: `Bearer ${token}`,
								"Content-Type": "application/json",
							},
						}
					);

					if (response.ok) {
						const data = await response.json();
						if (data.success && data.data?.user) {
							// Update user data with fresh data from server
							const user = data.data.user;
							setUserProfile({
								firstName: user.firstName || "",
								lastName: user.lastName || "",
								email: user.email || "",
								phone: user.phone || "",
								uid: user.extraId?.toString() || "",
								dateOfBirth: user.dateOfBirth || "",
								nationality: user.nationality || "",
								germanLevel: user.germanLevel || "",
								englishLevel: user.englishLevel || "",
								taxClass: user.taxClass || "",
								employmentType: user.employmentType || "",
								religion: user.religion || "",
								socialSecurityNumber: user.socialSecurityNumber || "",
								iban: user.iban || "",
								accountHolder: user.accountHolder || "",
								healthCertificate: user.healthCertificate || "",
								company: user.company || "",
								department: user.department || "",
							});
							localStorage.setItem("currentUser", JSON.stringify(user));
						}
					} else if (response.status === 401) {
						// Token might be expired, try to refresh it first
						const refreshToken = localStorage.getItem("refreshToken");
						if (refreshToken) {
							try {
								const refreshResponse = await fetch(
									`${import.meta.env.VITE_API_URL || "http://localhost:3001/api"}/auth/refresh`,
									{
										method: "POST",
										headers: { "Content-Type": "application/json" },
										body: JSON.stringify({ refreshToken }),
									}
								);

								if (refreshResponse.ok) {
									const refreshResult = await refreshResponse.json();
									// Handle both wrapped and unwrapped response formats
									const refreshData = refreshResult.data || refreshResult;

									if (refreshData.accessToken && refreshData.refreshToken) {
										// Successfully refreshed tokens
										localStorage.setItem(
											"accessToken",
											refreshData.accessToken
										);
										localStorage.setItem(
											"refreshToken",
											refreshData.refreshToken
										);
										// Don't logout, the refreshed token will be used on next API call
										console.log("Token refreshed successfully");
										return;
									} else {
										console.error(
											"Invalid refresh response format:",
											refreshResult
										);
									}
								}
							} catch (refreshError) {
								console.error(
									"Token refresh failed in AppContext:",
									refreshError
								);
							}
						}

						// Only logout if refresh failed or no refresh token
						localStorage.removeItem("accessToken");
						localStorage.removeItem("refreshToken");
						localStorage.removeItem("currentUser");
						setIsAuthenticated(false);
						setCurrentScreen("auth-sign-in");
						setUserProfile(null);
					}
				} catch (error) {
					console.error("Failed to validate auth:", error);
				}
			}
		};

		if (hasToken) {
			validateAuth();
		}
	}, []);

	const updateTaskCompletion = (taskId: string, completed: boolean) => {
		setSupervisorTasks((prev) =>
			prev.map((task) => (task.id === taskId ? { ...task, completed } : task))
		);
	};

	const updateIDCheckState = (updates: Partial<IDCheckState>) => {
		setIdCheckState((prev) => ({ ...prev, ...updates }));
	};

	const completeIDCheck = () => {
		const now = new Date();
		setIdCheckState((prev) => ({
			...prev,
			status: "Completed",
			uploadDate: formatDateDisplay(now),
			navigationContext: "initial", // Reset navigation context after completion
		}));

		// Update user completion state for onboarding progress
		updateUserCompletionState({
			id_check_complete: true,
		});

		// Navigate back to source screen
		if (navigationContext?.sourceScreen) {
			setCurrentScreen(navigationContext.sourceScreen);
		} else {
			setCurrentScreen("app-profile-completion-overview");
		}
	};

	const deleteIDDocument = () => {
		setIdCheckState({
			status: "Pending",
			replacementsRemaining: 2,
			navigationContext: "initial",
		});
	};

	const useReplacement = (): boolean => {
		if (idCheckState.replacementsRemaining > 0) {
			setIdCheckState((prev) => ({
				...prev,
				replacementsRemaining: prev.replacementsRemaining - 1,
			}));
			return true;
		}
		return false;
	};

	const startReplacement = () => {
		setIdCheckState((prev) => ({
			...prev,
			navigationContext: "replacement",
		}));
	};

	const updateProcessState = (updates: Partial<ProcessCompletionState>) => {
		setProcessState((prev) => ({ ...prev, ...updates }));
	};

	const updateUserCompletionState = (updates: Partial<UserCompletionState>) => {
		setUserCompletionState((prev) => ({ ...prev, ...updates }));
	};

	const updateOnboardingState = (updates: Partial<OnboardingState>) => {
		setOnboardingState((prev) => ({ ...prev, ...updates }));
	};

	const isFirstTimeUser = (): boolean => {
		return (
			onboardingState.isFirstTimeUser && !onboardingState.hasCompletedOnboarding
		);
	};

	const getProfileCompletionProgress = () => {
		const profileTasks = [
			"profile_complete",
			"contract_complete",
			"payroll_complete",
			"id_check_complete",
		];

		const completed = profileTasks.filter(
			(task) => userCompletionState[task as keyof UserCompletionState]
		).length;
		const total = 4; // Note: Code of conduct is now tracked separately from backend
		const percentage = Math.round((completed / total) * 100);

		return { completed, total, percentage };
	};

	// Dashboard tasks function - defines all tasks available on dashboard
	const getDashboardTasks = (): TaskItem[] => {
		return [
			// Main dashboard tasks (will be displayed as full cards)
			{
				id: "profile-completion",
				title: "taskDashboard.tasks.profileCompletion",
				status: userCompletionState.profile_complete
					? "completed"
					: "action-required",
				screen: "app-profile-completion-overview",
				showProgress: false,
			},
			{
				id: "apply-event",
				title: "taskDashboard.tasks.applyForEvent",
				status: userCompletionState.event_applied
					? "completed"
					: "action-required",
				screen: "app-event-application",
				showProgress: false,
			},
			{
				id: "event-details-combined",
				title: "taskDashboard.tasks.seeEventDetails",
				status: "info",
				screen: "my-event",
				showProgress: false,
			},
			// Group section - restored
			{
				id: "your-group",
				title: "taskDashboard.tasks.adjustYourGroup",
				status: userCompletionState.group_member
					? "completed"
					: "action-required",
				screen: "app-group",
				showProgress: false,
			},
			// Contract section - available for all users (will be filtered in TaskDashboard for internal users)
			{
				id: "contract",
				title: "taskDashboard.tasks.contract",
				status: userCompletionState.contract_complete
					? "completed"
					: "action-required",
				screen: "app-contract",
				showProgress: false,
			},
			// Payroll section - available for all users (will be filtered in TaskDashboard for internal users)
			{
				id: "payroll",
				title: "taskDashboard.tasks.payroll",
				status: userCompletionState.payroll_complete
					? "completed"
					: "action-required",
				screen: "app-payroll-info",
				showProgress: false,
			},
			// Settings moved back to main tasks (was in bottom icons)
			{
				id: "settings",
				title: "common.settings",
				status: "info",
				screen: "app-settings",
				showProgress: false,
			},
		];
	};

	// Mock implementations for required functions
	const checkFirstTimeOnboarding = () => {
		if (isFirstTimeUser()) {
			setCurrentScreen("onboarding-welcome");
		}
	};

	const getOnboardingSequence = (): OnboardingStepInfo[] => [
		{
			step: 1,
			id: "profile",
			title: "Complete your profile",
			route: "onboarding-step-profile",
			nextStep: 2,
		},
		{
			step: 2,
			id: "code-of-conduct",
			title: "Agree to Code of Conduct",
			route: "onboarding-step-code-of-conduct",
			nextStep: 3,
		},
		{
			step: 3,
			id: "event-application",
			title: "Apply for event",
			route: "onboarding-step-event-application",
			nextStep: null, // Last step
		},
	];

	const getCurrentStepInfo = (
		stepNumber: number
	): OnboardingStepInfo | undefined => {
		return getOnboardingSequence().find((step) => step.step === stepNumber);
	};

	const goToNextStep = (currentStep: number) => {
		const stepInfo = getCurrentStepInfo(currentStep);

		if (stepInfo?.nextStep) {
			const nextStepInfo = getCurrentStepInfo(stepInfo.nextStep);
			if (nextStepInfo) {
				updateOnboardingState({ currentOnboardingStep: stepInfo.nextStep });
				setCurrentScreen(nextStepInfo.route);
			}
		} else {
			// Last step completed, finish onboarding
			completeOnboarding();
		}
	};

	const continueToNextStep = (currentStep: number, taskData: any = null) => {
		saveTaskCompletion(currentStep, taskData, false);
		goToNextStep(currentStep);
	};

	const skipToNextStep = (currentStep: number) => {
		saveTaskCompletion(currentStep, null, true);
		goToNextStep(currentStep);
	};

	const saveTaskCompletion = (
		stepNumber: number,
		data: any,
		isSkipped: boolean
	) => {
		const stepInfo = getCurrentStepInfo(stepNumber);

		if (!stepInfo) return;

		const progressData: OnboardingStepProgress = {
			stepNumber: stepNumber,
			completed: !isSkipped,
			skipped: isSkipped,
			data: data,
			completedAt: new Date().toISOString(),
		};

		setOnboardingState((prev) => ({
			...prev,
			onboardingProgress: {
				...prev.onboardingProgress,
				[stepInfo.id]: progressData,
			},
			skippedSteps: isSkipped
				? [...prev.skippedSteps.filter((id) => id !== stepInfo.id), stepInfo.id]
				: prev.skippedSteps.filter((id) => id !== stepInfo.id),
		}));
	};

	const completeOnboarding = () => {
		updateOnboardingState({
			hasCompletedOnboarding: true,
			isFirstTimeUser: false,
			currentOnboardingStep: 0,
		});

		setCurrentScreen("dashboard");
	};

	const getAccessRequirements = (screen: AppScreen): string[] => {
		const accessRules: Record<string, string[]> = {
			dashboard: ["authenticated"],
			"my-event": ["authenticated"],
			"app-profile-completion": ["authenticated"],
			"app-payroll-data": ["authenticated"],
			"app-contract-data": ["authenticated"],
			"app-event-application": ["authenticated"],
			"app-code-of-conduct": ["authenticated"],
			"app-payroll-info": ["authenticated"],
			"app-group": ["authenticated"],
			"app-notifications": ["authenticated"],
			"app-profile-completion-overview": ["authenticated"],
			"app-prm-data": ["authenticated"],
			"app-contract": ["authenticated"],
			"app-info-faq": ["authenticated"],
			"app-settings": ["authenticated"],
		};

		return accessRules[screen] || ["authenticated"];
	};

	const canAccessScreen = (
		screen: AppScreen
	): {
		allowed: boolean;
		message?: string;
		redirectTo?: AppScreen;
		requirements?: string[];
	} => {
		if (screen.startsWith("auth-")) {
			if (isAuthenticated) {
				return {
					allowed: false,
					message: "Already authenticated",
					redirectTo: "dashboard",
				};
			}
			return { allowed: true };
		}

		if (!isAuthenticated) {
			return {
				allowed: false,
				message: "Authentication required",
				redirectTo: "auth-sign-in",
			};
		}

		return { allowed: true };
	};

	const navigateWithProcessCheck = (screen: AppScreen) => {
		const accessCheck = canAccessScreen(screen);
		if (accessCheck.allowed) {
			setCurrentScreen(screen);
		} else {
			console.log(`Access denied: ${accessCheck.message}`);
			if (accessCheck.redirectTo) {
				setCurrentScreen(accessCheck.redirectTo);
			}
		}
	};

	const navigateDirectly = (screen: AppScreen) => {
		setCurrentScreen(screen);
	};

	// New function to ensure navigation returns to profile completion
	const navigateToProfileCompletion = () => {
		setCurrentScreen("app-profile-completion");
	};

	// Enhanced navigation function that tracks return destination
	const navigateWithReturnTo = (
		screen: AppScreen,
		returnScreen: AppScreen = "app-profile-completion"
	) => {
		// Store the return destination in context
		setNavigationContext({
			sourceScreen: returnScreen,
			sourceEventId: undefined,
			selectedJobIndex: undefined,
		});
		setCurrentScreen(screen);
	};

	const getCurrentStep = () => {
		return { step: 1, title: "Dashboard", description: "Main dashboard" };
	};

	const triggerAutomaticBooking = () => {
		console.log("Automatic booking triggered");
	};

	const updateContractState = (updates: Partial<ContractState>) => {
		setContractState((prev) => ({ ...prev, ...updates }));
	};

	const downloadContract = () => {
		console.log("Download contract");
	};

	const markContractAsSent = () => {
		console.log("Mark contract as sent");
	};

	const downloadUCMContract = () => {
		console.log("Download UCM contract");
	};

	const completeContractProcess = () => {
		console.log("Complete contract process");
	};

	const initializeExistingUser = () => {
		console.log("Initialize existing user");
	};

	const loadExistingUserData = () => {
		console.log("Load existing user data");
	};

	const navigateToMyEventWithContext = () => {
		setCurrentScreen("my-event");
	};

	const getEventStatus = (eventIndex: number) => {
		return "unavailable" as const;
	};

	const getCombinedEventStatus = () => {
		return "unavailable" as const;
	};

	const getCombinedEventBadge = () => {
		return "Unavailable";
	};

	const getUserGroupStatus = () => {
		// Check localStorage for existing group
		const storedGroup = localStorage.getItem("userGroup");
		const storedGroupState = localStorage.getItem("groupState");
		const participationType = localStorage.getItem("participationType");

		if (participationType === "solo") {
			return { groupState: "solo" as GroupState, group: null, isSolo: true };
		}

		if (storedGroup && storedGroupState) {
			try {
				const group: Group = JSON.parse(storedGroup);
				const groupState = storedGroupState as GroupState;
				return { groupState, group, isSolo: false };
			} catch (error) {
				console.error("Error parsing stored group:", error);
				return {
					groupState: "no-group" as GroupState,
					group: null,
					isSolo: false,
				};
			}
		}

		return { groupState: "no-group" as GroupState, group: null, isSolo: false };
	};

	const createGroup = async (
		groupName: string,
		description: string,
		privacy: "public" | "private"
	): Promise<Group> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 1000));

		// Generate random group number
		const groupNumber = Math.floor(10000 + Math.random() * 90000).toString();

		// Create group object
		const newGroup: Group = {
			id: `group_${Date.now()}`,
			name: groupName,
			description: description,
			groupNumber: groupNumber,
			privacy: privacy,
			ownerId: userProfile?.uid || "current_user",
			members: [
				{
					id: userProfile?.uid || "current_user",
					name: userProfile
						? `${userProfile.firstName} ${userProfile.lastName}`
						: "You",
					email: userProfile?.email || "user@example.com",
					isOwner: true,
				},
			],
			maxMembers: 4,
		};

		// Store group in localStorage for persistence
		localStorage.setItem("userGroup", JSON.stringify(newGroup));
		localStorage.setItem("groupState", "owner");

		// Update user completion state
		updateUserCompletionState({
			group_member: true,
		});

		return newGroup;
	};

	const joinGroup = async (groupNumber: string): Promise<Group> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 1000));

		// Check if group exists (simulate 70% success rate)
		const groupExists = Math.random() > 0.3;

		if (!groupExists) {
			throw new Error("Group not found. Please check the group number.");
		}

		// Simulate group being full (10% chance)
		const groupFull = Math.random() < 0.1;

		if (groupFull) {
			throw new Error(
				"This group is full (4/4 members). Try another group number."
			);
		}

		// Create mock group with existing members
		const existingGroup: Group = {
			id: `group_${groupNumber}`,
			name: `Airport Team ${groupNumber}`,
			description: "A dedicated team for Frankfurt Airport security operations",
			groupNumber: groupNumber,
			privacy: Math.random() > 0.5 ? "public" : "private",
			ownerId: "group_owner_id",
			members: [
				{
					id: "group_owner_id",
					name: "Group Leader",
					email: "leader@example.com",
					isOwner: true,
				},
				// Add 1-2 random existing members
				...Array.from(
					{ length: Math.floor(Math.random() * 2) + 1 },
					(_, i) => ({
						id: `member_${i + 1}`,
						name: `Team Member ${i + 1}`,
						email: `member${i + 1}@example.com`,
						isOwner: false,
					})
				),
				// Add current user
				{
					id: userProfile?.uid || "current_user",
					name: userProfile
						? `${userProfile.firstName} ${userProfile.lastName}`
						: "You",
					email: userProfile?.email || "user@example.com",
					isOwner: false,
				},
			],
			maxMembers: 4,
		};

		// Store group in localStorage for persistence
		localStorage.setItem("userGroup", JSON.stringify(existingGroup));
		localStorage.setItem("groupState", "member");

		// Update user completion state
		updateUserCompletionState({
			group_member: true,
		});

		return existingGroup;
	};

	const leaveGroup = async (): Promise<void> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 800));

		// Remove group from localStorage
		localStorage.removeItem("userGroup");
		localStorage.removeItem("groupState");

		// Update user completion state
		updateUserCompletionState({
			group_member: false,
		});
	};

	const deleteGroup = async (): Promise<void> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 800));

		// Remove group from localStorage
		localStorage.removeItem("userGroup");
		localStorage.removeItem("groupState");

		// Update user completion state
		updateUserCompletionState({
			group_member: false,
		});
	};

	const kickMember = async (memberId: string): Promise<void> => {
		// Simulate API call delay
		await new Promise((resolve) => setTimeout(resolve, 500));

		// Get current group
		const storedGroup = localStorage.getItem("userGroup");
		if (storedGroup) {
			try {
				const group: Group = JSON.parse(storedGroup);

				// Remove member from group
				const updatedGroup: Group = {
					...group,
					members: group.members.filter((member) => member.id !== memberId),
				};

				// Save updated group
				localStorage.setItem("userGroup", JSON.stringify(updatedGroup));
			} catch (error) {
				console.error("Error updating group after kicking member:", error);
			}
		}
	};

	const setSoloParticipation = () => {
		// Set solo participation preference
		localStorage.setItem("participationType", "solo");

		// Remove any existing group data
		localStorage.removeItem("userGroup");
		localStorage.removeItem("groupState");

		// Update user completion state
		updateUserCompletionState({
			group_member: true, // Solo participation counts as having "group" status
		});
	};

	// Comprehensive logout function that clears all storage
	const logout = () => {
		// Capture extraId before clearing localStorage
		const storedUser = localStorage.getItem("currentUser");
		let extraId: number | undefined;
		if (storedUser) {
			try {
				const user = JSON.parse(storedUser);
				extraId = user.extraId;
			} catch (error) {
				console.warn("Failed to parse stored user data:", error);
			}
		}

		// Clear authentication tokens
		localStorage.removeItem("accessToken");
		localStorage.removeItem("refreshToken");
		localStorage.removeItem("currentUser");

		// Clear user-specific data
		localStorage.removeItem("userGroup");
		localStorage.removeItem("groupState");
		localStorage.removeItem("participationType");
		localStorage.removeItem("hasSeenProfileIntro");

		// Clear data accuracy confirmations if we have the user data
		if (extraId) {
			clearExtraConfirmations(extraId);
		}

		// NOTE: We do NOT clear marketingDatesModalShown_ keys
		// These are tied to extraId and should persist across login sessions
		// so users don't see the marketing modal repeatedly

		// Clear session storage as well
		sessionStorage.clear();

		// Reset all state
		setIsAuthenticated(false);
		setCurrentScreen("auth-sign-in");
		setUserProfile(null);
	};

	return (
		<AppContext.Provider
			value={{
				currentScreen,
				setCurrentScreen,
				userProfile,
				setUserProfile,
				jobs,
				setJobs,
				selectedJob,
				setSelectedJob,
				contracts,
				setContracts,
				payrollDocuments,
				setPayrollDocuments,
				appointments,
				setAppointments,
				staffMembers,
				setStaffMembers,
				supervisorTasks,
				setSupervisorTasks,
				updateTaskCompletion,
				isAuthenticated,
				setIsAuthenticated,
				idCheckState,
				updateIDCheckState,
				completeIDCheck,
				deleteIDDocument,
				useReplacement,
				startReplacement,
				processState,
				updateProcessState,
				canAccessScreen,
				navigateWithProcessCheck,
				navigateDirectly,
				navigateToProfileCompletion,
				navigateWithReturnTo,
				getCurrentStep,
				triggerAutomaticBooking,
				userType,
				setUserType,
				contractState,
				updateContractState,
				downloadContract,
				markContractAsSent,
				downloadUCMContract,
				completeContractProcess,
				initializeExistingUser,
				loadExistingUserData,
				navigationContext,
				setNavigationContext,
				navigateToMyEventWithContext,
				userCompletionState,
				updateUserCompletionState,
				getDashboardTasks,
				getEventStatus,
				getCombinedEventStatus,
				getCombinedEventBadge,
				onboardingState,
				updateOnboardingState,
				isFirstTimeUser,
				getProfileCompletionProgress,
				checkFirstTimeOnboarding,
				getOnboardingSequence,
				getCurrentStepInfo,
				goToNextStep,
				continueToNextStep,
				skipToNextStep,
				saveTaskCompletion,
				completeOnboarding,
				getAccessRequirements,
				getUserGroupStatus,
				createGroup,
				joinGroup,
				leaveGroup,
				deleteGroup,
				kickMember,
				setSoloParticipation,
				logout,
			}}
		>
			{children}
		</AppContext.Provider>
	);
}

export function useApp() {
	const context = useContext(AppContext);
	if (context === undefined) {
		throw new Error("useApp must be used within an AppProvider");
	}
	return context;
}
